import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Message } from '../AiChatSlideout';
import styles from './ChatMessage.module.scss';

interface ChatMessageProps {
  message: Message;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const isUser = message.role === 'user';

  const formatTime = (date: Date) => {
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <div className={`${styles.message} ${isUser ? styles.user : styles.assistant}`}>
      <div className={styles.bubble}>
        <div className={styles.text}>
          {message.role === 'assistant' ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Custom styling for markdown elements
                p: ({ children, ...props }) => <p {...props} className={styles.paragraph}>{children}</p>,
                strong: ({ children, ...props }) => <strong {...props} className={styles.bold}>{children}</strong>,
                em: ({ children, ...props }) => <em {...props} className={styles.italic}>{children}</em>,
                ul: ({ children, ...props }) => <ul {...props} className={styles.list}>{children}</ul>,
                ol: ({ children, ...props }) => <ol {...props} className={styles.orderedList}>{children}</ol>,
                li: ({ children, ...props }) => <li {...props} className={styles.listItem}>{children}</li>,
                code: ({ children, ...props }: any) => {
                  const inline = props.inline || !props.className?.includes('language-');
                  return inline ? 
                    <code {...props} className={styles.inlineCode}>{children}</code> : 
                    <pre className={styles.codeBlock}><code {...props}>{children}</code></pre>;
                },
                blockquote: ({ children, ...props }) => <blockquote {...props} className={styles.blockquote}>{children}</blockquote>,
                table: ({ children, ...props }) => (
                  <div className={styles.tableWrapper}>
                    <table {...props} className={styles.table}>{children}</table>
                  </div>
                ),
                th: ({ children, ...props }) => <th {...props} className={styles.tableHeader}>{children}</th>,
                td: ({ children, ...props }) => <td {...props} className={styles.tableCell}>{children}</td>,
                h1: ({ children, ...props }) => <h1 {...props} className={styles.heading1}>{children}</h1>,
                h2: ({ children, ...props }) => <h2 {...props} className={styles.heading2}>{children}</h2>,
                h3: ({ children, ...props }) => <h3 {...props} className={styles.heading3}>{children}</h3>,
                h4: ({ children, ...props }) => <h4 {...props} className={styles.heading4}>{children}</h4>,
                h5: ({ children, ...props }) => <h5 {...props} className={styles.heading5}>{children}</h5>,
                h6: ({ children, ...props }) => <h6 {...props} className={styles.heading6}>{children}</h6>,
              }}
            >
              {message.content}
            </ReactMarkdown>
          ) : (
            message.content
          )}
        </div>
        <div className={styles.time}>
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  );
};